import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { useUsers } from '@/hooks/useUsers';
import { useClients } from '@/hooks/useClients';
import { useGroups } from '@/hooks/useGroups';
import { useLicenseValidation } from '@/hooks/useLicenseValidation';
import { UserFormFields } from './UserFormFields';
import { UserFormActions } from './UserFormActions';
import { LicenseUsageAlert } from './LicenseUsageAlert';
import { useToast } from '@/components/ui/use-toast';

const userSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  phone: z.string().optional(),
  department: z.string().optional(),
  role: z.string().min(1, 'Função é obrigatória'),
  client_id: z.string().optional(),
  client_ids: z.array(z.string()).optional(),
  status: z.enum(['active', 'inactive']),
  groups: z.array(z.string()).optional(),
});

type UserFormData = z.infer<typeof userSchema>;

interface NewUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NewUserDialog = ({ open, onOpenChange }: NewUserDialogProps) => {
  const { addUser } = useUsers();
  const { clients } = useClients();
  const { groups } = useGroups();
  const { validateUserAddition } = useLicenseValidation();

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      department: '',
      role: 'user',
      client_id: 'none',
      client_ids: [],
      status: 'active',
      groups: [],
    },
  });

  const { toast } = useToast();

  const selectedRole = form.watch('role');
  const selectedClientId = form.watch('client_id');

  const onSubmit = async (data: UserFormData) => {
    // Validar limites de licença antes de criar
    if (data.role && data.client_id && data.client_id !== 'none') {
      const validation = await validateUserAddition(data.role, data.client_id);
      if (!validation.isValid) {
        toast({
          title: "Limite de licença atingido",
          description: validation.message,
          variant: "destructive",
        });
        return;
      }
    }

    // Garantir que os campos obrigatórios estão presentes
    const userData = {
      name: data.name,
      email: data.email,
      phone: data.phone,
      department: data.department,
      role: data.role,
      client_id: data.client_id,
      client_ids: data.client_ids,
      status: data.status,
      groups: data.groups,
    };
    
    try {
      await addUser.mutateAsync(userData);
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
    }
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Novo Usuário</DialogTitle>
          <DialogDescription>Preencha os dados para cadastrar um novo usuário.</DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Alerta de uso da licença se cliente e role selecionados */}
            {selectedClientId && selectedClientId !== 'none' && selectedRole && (
              <LicenseUsageAlert clientId={selectedClientId} />
            )}
            
            <UserFormFields 
              control={form.control} 
              clients={clients} 
              groups={groups}
            />
            <UserFormActions 
              onCancel={handleCancel}
              isSubmitting={form.formState.isSubmitting}
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};